import { SelectFieldSubMenuProps } from "./SelectFieldSubMenu.tsx";
import { IdNamePair } from "../../../../customLists/customListView/CustomListView.types.ts";
import { isNullOrWhitespace } from "../../../stringOperations.ts";
import { useCallback, useState } from "react";
import { useDebounce } from "../../../hooks/useDebounce.hooks.ts";
import { GetListData } from "./SubMenuDataHelper.ts";
import { EntityType } from "../../../Common.types.ts";
import { SearchResultViewModel } from "../../search/SearchResultList.types.ts";

import { useDotLegalQuery, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";

export function useSelectFieldSubMenu(props: SelectFieldSubMenuProps) {
    const [search, setSearch] = useState<string>("");
    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

    const debouncedSetSearch = useDebounce((value: string) => {
        setDebouncedSearchValue(value);
    }, 1000);

    const queryForSearch = useDotLegalQueryWithKey<Array<SearchResultViewModel>>(
        `filter-popover-search-query-${props.selectedOption.entityType}-${debouncedSearchValue}`,
        getQueryUrl(),
        {
            enabled: props.selectedOption.entityType !== undefined,
            staleTime: 15000,
        }
    );

    function getQueryUrl() {
        return `/search?query=${search}&fetchUsers=false&entityType=${props.selectedOption.entityType}`;
    }

    const { isLoading, data: fetchedData } = useDotLegalQueryWithKey<Array<IdNamePair>>(
        props.selectedOption?.dataUrl ?? "undefined",
        props.selectedOption!.dataUrl!,
        {
            enabled: !isNullOrWhitespace(props.selectedOption?.dataUrl) && props.selectedOption.entityType === undefined,
            staleTime: 15000,
        }
    );

    const hasSearchUrl = !isNullOrWhitespace(props.selectedOption.searchUrl);
    const customSearchQuery = useDotLegalQueryWithKey<Array<IdNamePair>>(
        `filter-popover-search-url-${props.selectedOption.id}-${debouncedSearchValue}`,
        `${props.selectedOption.searchUrl}?query=${search}`,
        {
            enabled: hasSearchUrl,
            staleTime: 15000,
        }
    );

    let chosenValues = GetListData(props.selectedOption.value);

    const getData = () => {
        if (props.selectedOption.fixedData !== undefined && props.selectedOption.fixedData?.length > 0) {
            return props.selectedOption.fixedData;
        }

        if (props.selectedOption.entityType !== undefined) {
            return (
                queryForSearch.data?.map((x) => {
                    return { id: x.entityId, name: x.entityName };
                }) ?? []
            );
        }

        if (hasSearchUrl) {
            return customSearchQuery.data ?? [];
        }

        return fetchedData ?? [];
    };

    const onSearchChange = useCallback(
        (value: string) => {
            setSearch(value); // Update immediately for UI responsiveness
            debouncedSetSearch(value); // Trigger debounced API call
        },
        [debouncedSetSearch]
    );

    const data = getData();
    return {
        isLoading: isLoading || queryForSearch.isLoading || customSearchQuery.isLoading,
        hasNoData: data === undefined || data.length === 0,
        data,
        chosenValues,
        onSearchChange,
        search,
    };
}
